import { Component, EventEmitter, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { AnessaTranslationService } from '../translation/anessa-translation.service';
import { UserpilotReloader } from '../userpilot-reloader.component';
import * as Plotly from 'plotly.js'

@Component({
  selector: 'app-plotly',
  templateUrl: './plotly.component.html',
  styleUrls: ['./plotly.component.scss']
})
export class PlotlyComponent extends UserpilotReloader implements OnInit, OnDestroy {
  onDestroyNotifier = new EventEmitter<boolean>();

  @ViewChild('thePlot') thePlot;
  @Input() graphType: string = '';
  @Input() graphData?: Object[];
  @Input() graphTitle: string = '';
  @Input() view?: number[];
  @Input() xAxisLabel: string = '';
  @Input() yAxisLabel: string = '';
  @Input() precision: number = -1;
  @Input() legend: any = {'orientation': 'h', x: -0.05, y: -0.3};

  data: PieData[] | GraphData[] = [];

  // allow custom layouts
  @Input() layout?: Partial<Plotly.Layout> = {
    xaxis: {},
    yaxis: {}
  };
  @Input() maxTicks = 60;
  config = {
    displaylogo: false,
    responsive: true,
    modeBarButtonsToRemove: ['select2d', 'lasso2d', 'lasso3d', 'resetScale2d']
  };

  constructor(
    private translationService: AnessaTranslationService,
  ) {
    super();
  }

  addEntryData: Function = function (entry: Object, index: number, everyNth: number) {};

  ngOnInit() {
    this.buildGraph();
    this.translateData();

    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.translateData();
      });
  }

  ngOnDestroy() {
    this.onDestroyNotifier.next(true);
    this.onDestroyNotifier.unsubscribe();
  }

  translateData() {
    this.data.forEach((entry: any) => {
      if (entry.name) {
        entry.name = this.translationService.get(entry.name);
      }
    });
  }

  buildGraph() {
    if (!this.layout) {
      this.layout = {
        autosize: true,
        xaxis: {title: ''},
        yaxis: {title: ''},
        title: '',
        legend: this.legend,
        margin: {l: 50, r: 0, b: 70, t: 50}
      };
    }
    if (!this.layout.xaxis || !this.layout.yaxis) return;
    this.layout.legend = { ...this.layout.legend, ...this.legend };

    // set graph data
    this.setGraphType();
    this.setGraphData();
    // set graph size
    if (this.view && this.view.length === 2) {
      this.layout.width = this.view[0];
      this.layout.height = this.view[1];
    }
    // set axes labels
    this.layout.xaxis.title = this.xAxisLabel;
    this.layout.yaxis.title = this.yAxisLabel;
    this.layout.title = this.graphTitle;
    if (this.precision > -1) {
      this.layout.xaxis.tickformat = `.${this.precision}f`;
      this.layout.yaxis.tickformat = `.${this.precision}f`;
    }
  }

  setGraphType() {
    if (!this.layout?.xaxis || !this.layout.yaxis) return;

    switch (this.graphType) {
      case 'bar':
        this.data = [
          {x: [], y: [], type: 'bar', name: ''}
        ];
        this.data[0].x = [];
        this.data[0].y = [];
        this.layout.yaxis['rangemode'] = 'tozero';
        this.layout.xaxis['tickvals'] = [];
        this.layout.xaxis['ticktext'] = [];
        this.addEntryData = this.addEntryToData;
        break;
      case 'bargroup':
        this.data = [];
        this.layout.yaxis['rangemode'] = 'tozero';
        this.layout.xaxis['tickvals'] = [];
        this.layout.xaxis['ticktext'] = [];
        this.addEntryData = this.addSeriesToData;
        break;
      case 'linechart':
        this.data = [];
        this.layout.yaxis['rangemode'] = 'tozero';
        this.addEntryData = this.addScatterToData;
        break;
      case 'stackedbar':
        this.layout.yaxis['rangemode'] = 'tozero';
        this.layout['barmode'] = 'stack';
        this.data = [];
        this.layout.yaxis['rangemode'] = 'tozero';
        this.addEntryData = this.addGroupScatterToData;
        break;
      case 'piechart':
        this.data = [new PieData([], [], 'pie', [])];
        this.addEntryData = this.addPieToData;
        break;
      case 'multitrace':
        this.data = [];
        this.layout.yaxis['rangemode'] = 'tozero';
        this.addEntryData = this.addGroupScatterToData;
        break;
      case 'raw':
        this.data = [];
        this.layout.yaxis['rangemode'] = 'tozero';
        this.addEntryData = this.addGroupScatterToData;
        break;

    }
  }

  setGraphData() {
    if (!this.graphData) {
      return;
    }
    const everyNth = Math.ceil(this.graphData.length / this.maxTicks);
    this.graphData.forEach((entry, index) => {
      this.addEntryData(entry, index, everyNth);
    });
    this.swapInterpolation();
  }

  // swap bmp/yield and interpolation to put bmp/yield infront
  swapInterpolation() {
    if (this.graphType !== 'linechart' || this.data.length < 2) {
      return;
    }
    const dataName = this.data[0]['name'];
    const indexes = {interpolation: -1};
    indexes[dataName] = -1;
    this.data.forEach((aData, index) => {
      if (indexes[aData.name]) {
        indexes[aData.name] = index;
      }
    });
    if (indexes[dataName] < indexes.interpolation) {
      const bmp = {...this.data[indexes[dataName]]};
      this.data[indexes[dataName]] = {...this.data[indexes.interpolation]};
      this.data[indexes.interpolation] = bmp;
    }
    // styling
    [indexes[dataName], indexes.interpolation] = [indexes.interpolation, indexes[dataName]];
    this.data[indexes[dataName]]['marker']['color'] = 'rgba(0, 142, 192, 0.7)';
    this.data[indexes.interpolation]['marker']['color'] = 'rgba(92, 174, 74, 0.7)';
  }

  addEntryToData(entry: Object, index: number, everyNth: number) {
    if (this.data[0] instanceof GraphData) {
      this.data[0].x.push(index + 1);
      this.data[0].y.push(entry['value']);
      this.updateTickX(index, everyNth, entry);
    }
  }

  addSeriesToData(entry: { name: string, series: Array<Object> }, index: number, everyNth: number) {
    entry['series'].forEach(series => {
      const tempData = this.getDataByName(series['name'], 'bar');
      tempData['x'].push(index + 1);
      tempData['y'].push(series['value']);
    });
    this.updateTickX(index, everyNth, entry);
  }

  updateTickX(index: number, everyNth: number, entry) {
    if (!this.layout?.xaxis) return;
    if (index % everyNth === 0) {
      if (!this.layout.xaxis.tickvals) {
        this.layout.xaxis.tickvals = [];
      }
      if (!this.layout.xaxis.ticktext) {
        this.layout.xaxis.ticktext = [];
      }
      this.layout.xaxis.tickvals.push(index + 1);
      this.layout.xaxis.ticktext.push(entry['name']);
    }
  }

  addScatterToData(entry: { name: string, series: Array<Object> }, index: number, everyNth: number) {
    const tempData = this.getDataByName(entry['name'], 'scatter');
    if (entry.series.length === 0) {
      return;
    }
    tempData['marker'] = {size: entry.series[0]['r']};
    tempData['mode'] = 'markers';
    entry['series'].forEach(series => {
      tempData['x'].push(series['x']);
      tempData['y'].push(series['y']);
    });
  }

  addGroupScatterToData(entry, index: number, everyNth: number) {
    this.data.push(entry);
  }

  /**
   * Adds the provided entry to the local plotly data array.
   * @param entry is an object that will have a name for the entry, and a value (number)
   */
  addPieToData(entry: { name: string, value: number, unit: string}) {
    if (this.data && this.data.length > 0) {
      if (this.data[0] instanceof PieData) {
        this.data[0].values.push(entry.value);
        this.data[0].labels.push(entry.name);
        if (entry.unit) {
          this.data[0].hovertemplate.push('%{label}<br>' +
            '%{value} ' + entry.unit + '<br>' +
            '%{percent:,.2%}' +
            '<extra></extra>');
          this.data[0].texttemplate = '%{percent:,.2%}';
        } else {
          this.data[0].hovertemplate = [];
        }
      }

    }
  }

  getDataByName(name: string, type: string) {
    let tempData = null;
    this.data.forEach(data => {
      if (data.name === name) {
        tempData = data;
      }
    });
    if (!tempData) {
      tempData = {x: [], y: [], type: type, name: name};
      this.data.push(tempData);
    }
    return tempData;
  }
}

export class PieData {
  // removed the sort functionality that sorts the traces by size.
  // this ensures the feedstock are displayed in the order they are entered.
  public sort = false;
  constructor(public values: number[], public labels: string[], public type: string,
              public hovertemplate: string[], public texttemplate: string = ''
  ) {
  }
}

export class GraphData {
  constructor(public x: any[], public y: any[], public type: string, public name: string) {
  }
}
